import { Contracts } from './contracts/Contracts';
import { CorrelationVector } from './CorrelationVector';
import { logNotification, LogLevel, Category } from './OTelNotifications';
import { DataField } from './DataField';
import { ITelemetryLogger } from './TelemetryLogger';
import { OptionalEventFlags } from './OptionalEventFlags';

// Currently we only send one activity at a time
const ACTIVITY_COUNT = 1;

// Currently we do not aggregate activities in OTelJS
const ACTIVITY_AGGMODE = 0;

export interface IActivityScope {
  /**
   * Sets optional event flags.
   * @param eventFlags Event flags to set
   */
  setEventFlags(eventFlags: OptionalEventFlags): void;

  /**
   * Adds a data field to the activity
   * @param dataField A data field to add
   */
  addDataField(dataField: DataField): void;

  /**
   * Adds data fields to the activity
   * @param dataFields Data fields to add
   */
  addDataFields(dataFields: DataField[]): void;

  /**
   * Sets whether the activity succeeded
   * @param success Whether the activity succeeded
   */
  setSuccess(success: boolean): void;

  /**
   * Sets the result of the activity
   * @param resultCode The result code
   * @param resultType Optional: The result type
   * @param resultTag Optional: The result tag
   */
  setResult(resultCode: number, resultType?: string, resultTag?: number): void;

  /**
   * Ends the activity; sends a telemetry event.
   */
  endNow(): void;

  /**
   * Creates an activity parented to the current activity. Does not start the activity.
   * @param activityName The name of the activity
   */
  createChildActivity(activityName: string): IActivityScope;

  /**
   * Starts an activity to execute asynchronously
   * @param activityBody The body of the activity
   */
  executeAsync<T>(activityBody: (activity: IActivityScope) => Promise<T>): Promise<T>;

  /**
   * Starts an activity to execute synchronously
   * @param activityBody The body of the activity
   */
  executeSync<T>(activityBody: (activity: IActivityScope) => T): T;

  /**
   * Starts an activity to execute asynchronously, parented to the current activity.
   * @param activityName The name of the activity
   * @param activityBody The body of the activity
   */
  executeChildActivityAsync<T>(activityName: string, activityBody: (activity: IActivityScope) => Promise<T>): Promise<T>;

  /**
   * Starts an activity to execute synchronously, parented to the current activity.
   * @param activityName The name of the activity
   * @param activityBody The body of the activity
   */
  executeChildActivitySync<T>(activityName: string, activityBody: (activity: IActivityScope) => T): T;
}

let getCurrentMicroseconds = () => {
  return Date.now() * 1000;
};
if (typeof window.performance === 'object' && 'now' in window.performance) {
  getCurrentMicroseconds = () => {
    return Math.floor(window.performance.now()) * 1000;
  };
}

export class ActivityScope implements IActivityScope {
  private _cv: CorrelationVector.CV;
  private _activityName: string;
  private _dataFields: DataField[];
  private _startTime: number;
  private _success: boolean | undefined;
  private _result: Contracts.Office.System.Result.Contract | undefined;
  private _telemetryLogger: ITelemetryLogger;
  private _ended: boolean;
  private _optionalEventFlags: OptionalEventFlags = {};

  private constructor(telemetryLogger: ITelemetryLogger, activityName: string, parent?: ActivityScope) {
    this._ended = false;
    this._telemetryLogger = telemetryLogger;
    this._activityName = activityName;

    if (parent) {
      this._cv = CorrelationVector.getNextChild(parent._cv);
    } else {
      this._cv = CorrelationVector.getNext();
    }

    this._dataFields = [];
    this._success = undefined;
    this._startTime = getCurrentMicroseconds();
  }

  public static createNew(telemetryLogger: ITelemetryLogger, activityName: string) {
    return new ActivityScope(telemetryLogger, activityName);
  }

  public createChildActivity(activityName: string): IActivityScope {
    let childActivity = new ActivityScope(this._telemetryLogger, activityName, this);
    return childActivity;
  }

  public setEventFlags(eventFlags: OptionalEventFlags) {
    this._optionalEventFlags = eventFlags;
  }

  public addDataField(dataField: DataField) {
    this._dataFields.push(dataField);
  }

  public addDataFields(dataFields: DataField[]) {
    this._dataFields.push(...dataFields);
  }

  public setSuccess(success: boolean) {
    this._success = success;
  }

  public setResult(code: number, type?: string, tag?: number): void {
    this._result = {
      code: code,
      type: type,
      tag: tag
    };
  }

  public endNow(): void {
    if (this._ended) {
      logNotification(LogLevel.Error, Category.Core, () => 'Activity has already ended');
      return;
    }
    if (this._success === undefined && this._result === undefined) {
      // The Activity dashboard shows these activities as 'unresolved'
      // but developers generally should avoid leaving activities in this state.
      logNotification(LogLevel.Warning, Category.Core, () => 'Activity does not have success or result set');
    }

    let endTime = getCurrentMicroseconds();
    let duration = endTime - this._startTime;
    this._ended = true;

    let activity: Contracts.Office.System.Activity.Contract = {
      duration: duration,
      count: ACTIVITY_COUNT,
      aggMode: ACTIVITY_AGGMODE,
      cV: this._cv.getString(),
      success: this._success,
      result: this._result
    };

    return this._telemetryLogger.sendActivity(this._activityName, activity, this._dataFields, this._optionalEventFlags);
  }

  public async executeAsync<T>(activityBody: (activity: IActivityScope) => Promise<T>): Promise<T> {
    return activityBody(this)
      .then(result => {
        this.endNow();
        return result;
      })
      .catch(e => {
        this.endNow();
        throw e;
      });
  }

  public executeSync<T>(activityBody: (activity: IActivityScope) => T): T {
    try {
      let ret = activityBody(this);
      this.endNow();
      return ret;
    } catch (e) {
      this.endNow();
      throw e;
    }
  }

  public async executeChildActivityAsync<T>(activityName: string, activityBody: (activity: IActivityScope) => Promise<T>): Promise<T> {
    return this.createChildActivity(activityName).executeAsync(activityBody);
  }

  public executeChildActivitySync<T>(activityName: string, activityBody: (activity: IActivityScope) => T): T {
    return this.createChildActivity(activityName).executeSync(activityBody);
  }
}
